import { cliffFaceBetween, cliffLevelsOf, cliffStep } from "./cliff_mover"
import { BODY_GROUND_OFFSET } from "./lpc/body_placement"
import { CollisionMover, Coordinate, Entity } from "./models"
import { HillLevels, hillHeightAt } from "./room/hill_height"
import { STOREY_RISE_PX } from "./room/ember/two_storey_house"
import { TileMapMetaProvider } from "./tile_map_meta_provider"

/**
 * A SHOT AGAINST A CLIFF'S FACE (openspec world/cliffs). A shot flies over a hill as over flat ground, drawn on a straight
 * line from the ground under the bow to its aim's height (client hill_lift.ts). One that meets a cliff's face from below,
 * lower than its rim, stops there, struck into the face; one leaving a cliff's top over its rim flies on. Its flight
 * moves through `shotMover` on the server, on the client drawing it, and in the stop both work out (blockedStop), so
 * they agree on where it struck.
 *
 * A shot's position is a body's: BODY_GROUND_OFFSET above the ground it is over, as the bow's archer's is.
 */

/** Where a shot was aimed: the ground point, and for a cliff's face, how far up the face (drawn) the point is. */
export type ShotAim = Coordinate & { lift?: number }

/**
 * Where a flight that ran its whole reach comes down: exactly that far along its line from where it left
 * the bow. Not where the arrow was when its reach was found to be used up. That is a step further on, and
 * the step is the stepper's own -- a frame on the shooter's client, a pass on the server -- so the two
 * would put the arrow down up to a couple of dozen pixels apart.
 */
export const reachPoint = (flight: Pick<Entity, "origin" | "speed2" | "maxDistance">): Coordinate | undefined => {
    const { origin, speed2, maxDistance } = flight
    const speed = Math.hypot(speed2?.x ?? 0, speed2?.y ?? 0)
    if (!origin || maxDistance === undefined || !speed) return undefined
    return { x: origin.x + speed2.x / speed * maxDistance, y: origin.y + speed2.y / speed * maxDistance }
}

export type ShotFlight = Pick<Entity, "origin" | "speed2" | "maxDistance"> & { projectileTarget?: ShotAim }

/** The end of a shot's line: the point it was aimed at, or for one aimed at nothing, where its reach ends. */
export const shotAim = (flight: ShotFlight): ShotAim | undefined => flight.projectileTarget ?? reachPoint(flight)

/**
 * How high a shot at position `at` is drawn over the hill: on its straight line from the height of the ground under the
 * bow to its aim's (the face's height, aimed at a face), by how far along that line it has gone; never above the hill's
 * top nor under the ground. A shot with no line, the height under it.
 */
export const shotHeightAt = (levels: HillLevels, flight: ShotFlight, at: Coordinate): number => {
    const { origin } = flight, aim = shotAim(flight)
    if (!origin || !aim) return hillHeightAt(levels, at.x, at.y + BODY_GROUND_OFFSET)
    const from = hillHeightAt(levels, origin.x, origin.y + BODY_GROUND_OFFSET)
    const to = aim.lift ?? hillHeightAt(levels, aim.x, aim.y)
    return Math.min(levels.max * STOREY_RISE_PX, Math.max(0, from + (to - from) * Math.max(0, alongLine(origin, aim, at))))
}

/** How far along the line from `origin` to `aim` the point `at` is: 0 at the bow, 1 at the aim. */
const alongLine = (origin: Coordinate, aim: Coordinate, at: Coordinate): number => {
    const dx = aim.x - origin.x, dy = aim.y - origin.y, length = dx * dx + dy * dy
    return length > 0 ? ((at.x - origin.x) * dx + (at.y - origin.y) * dy) / length : 1
}

/**
 * The height a shot at `at` strikes a cliff's face at, moving by `step`: when the step takes the ground under it up a
 * cliff's face and the shot is lower there than the top, or, for one aimed at a point of a face, when it reaches that
 * point -- the face is there, however slantwise the shot meets it. Undefined when it meets no face, or clears it.
 */
export const shotFaceStrike = (levels: HillLevels, flight: ShotFlight, at: Coordinate, step: Coordinate): number | undefined => {
    const aim = flight.projectileTarget
    if (aim?.lift !== undefined && flight.origin &&
        alongLine(flight.origin, aim, at) < 1 && alongLine(flight.origin, aim, { x: at.x + step.x, y: at.y + step.y }) >= 1)
        return aim.lift
    const x0 = at.x, y0 = at.y + BODY_GROUND_OFFSET, x1 = x0 + step.x, y1 = y0 + step.y
    if (cliffStep(levels, x0, y0, x1, y1) !== "rise") return undefined
    const height = shotHeightAt(levels, flight, { x: at.x + step.x, y: at.y + step.y })
    return height < hillHeightAt(levels, x1, y1) ? height : undefined
}

// The march shotFaceBetween takes from a shot to a body: fine enough to find a face's line between them.
const BETWEEN_STEP_PX = 2

/**
 * Whether a cliff's face stands between a shot at `at` and a body at `body` (both positions): the shot strikes the face
 * on its way there, so the body is out of its reach however near it is -- a shot and a body are compared a body's
 * height above their ground, and an arrow struck just under a rim is beside a body standing on it.
 */
export const shotFaceBetween = (levels: HillLevels, flight: ShotFlight, at: Coordinate, body: Coordinate): boolean => {
    const dx = body.x - at.x, dy = body.y - at.y, steps = Math.ceil(Math.hypot(dx, dy) / BETWEEN_STEP_PX)
    const step = { x: dx / steps, y: dy / steps }
    for (let i = 0; i < steps; i++)
        if (shotFaceStrike(levels, flight, { x: at.x + step.x * i, y: at.y + step.y * i }, step) !== undefined) return true
    return false
}

/**
 * Whether a cliff's face keeps a body at `body` out of reach of a flight at `at` (both positions). A melee strike (one
 * drawn as nothing: a hand weapon's swing, a creature's bite or claw) reaches across a face neither way, from where its
 * striker stood (cliffFaceBetween), so a crab on the beach never bites a body on the top, nor a sword on the rim a body
 * on the beach. A shot is kept only from a body up a face from it (shotFaceBetween), and flies on down over a rim.
 */
export const faceStopsStrike = (levels: HillLevels, flight: ShotFlight & Pick<Entity, "spriteId">, at: Coordinate,
    body: Coordinate): boolean =>
    flight.spriteId === "none" && flight.origin
        ? cliffFaceBetween(levels, flight.origin, body) : shotFaceBetween(levels, flight, at, body)

/** `mover` for a shot's flight, stopped at a cliff's face it meets lower than the top. A room without a cliff: `mover`. */
export const shotCliffMover = (map: TileMapMetaProvider, mover: CollisionMover, flight: ShotFlight): CollisionMover => {
    const levels = cliffLevelsOf(map)
    if (!levels) return mover
    return (bounds, translate, result, adjust) => {
        if (translate.x || translate.y) {
            const at = { x: bounds.x1 + bounds.width / 2, y: bounds.y1 + bounds.height / 2 }
            // Held where it is, with no tile struck: the face is no tile, and whatever stands on the top above it is not hit.
            if (shotFaceStrike(levels, flight, at, translate) !== undefined) {
                result.collided = true
                result.adjust.x = at.x
                result.adjust.y = at.y
                return
            }
        }
        mover(bounds, translate, result, adjust)
    }
}
